import { usersClient } from '@/helpers/api/apiClients'
import { PaginationResponseOfViewUserDetailsResponse } from '@/helpers/api/WebApiClient'
import config from '@/config'
import { createAuthenticatedFetch } from '@/helpers/api/httpClient'
import type { IUserRepository, LoginSeatSelectionResponse, LoginSeatUser } from './contracts/IUserRepository'

export type { LoginSeatSelectionResponse, LoginSeatUser }

const apiFetch = createAuthenticatedFetch()

const parseJson = async <T>(res: Response): Promise<T> => {
	if (!res.ok) {
		const err = await res.json().catch(() => ({}))
		return Promise.reject(err)
	}
	const text = await res.text()
	if (!text) return text as T
	try {
		return JSON.parse(text) as T
	} catch {
		return text as T
	}
}

const downloadFromResponse = async (response: Response, fallbackFileName: string) => {
	const blob = await response.blob()
	const contentDisposition = response.headers.get('content-disposition') ?? ''
	const fileNameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition)
	const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '')) : fallbackFileName
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	link.remove()
	URL.revokeObjectURL(url)
}

/**
 * User service - abstraction over users API client.
 * Use this instead of importing usersClient directly for better testability.
 */
export const userService: IUserRepository = {
	getList: () => usersClient.getList(),
	search: (request) => usersClient.search(request),
	searchCustomers: async (request) => {
		const response = await apiFetch(`${config.API_URL}/api/users/customers/search`, {
			method: 'POST',
			body: JSON.stringify({ ...request }),
		})
		const data = await response.json()
		return PaginationResponseOfViewUserDetailsResponse.fromJS(data)
	},
	exportCustomers: async (format, request) => {
		const response = await apiFetch(`${config.API_URL}/api/users/customers/export`, {
			method: 'POST',
			body: JSON.stringify({
				...request,
				format,
			}),
		})

		await downloadFromResponse(response, `customers-export.${format}`)
	},
	getById: (id) => usersClient.getById(id),
	create: (request) => usersClient.create(request),
	adminCreateCustomer: (request) => usersClient.adminCreateCustomer(request),
	updateUser: (id, request) => usersClient.updateUser(id, request),
	changePasswordForcefully: (id, request) => usersClient.changePasswordForcefully(id, request),
	getRoles: (userId) => usersClient.getRoles(userId),
	assignRoles: (userId, request) => usersClient.assignRoles(userId, request),
	selfRegister: (request) => usersClient.selfRegister(request),
	confirmEmail: (userId, code) => usersClient.confirmEmail(userId, code),
	adminConfirmEmail: (id) => usersClient.adminConfirmEmail(id),
	resetPassword: (request) => usersClient.resetPassword(request),
	forgotPassword: (request) => usersClient.forgotPassword(request),
	getLoginSeatSelection: async () => {
		const response = await apiFetch(`${config.API_URL}/api/users/login-seats`)
		return parseJson<LoginSeatSelectionResponse>(response)
	},
	selectLoginSeats: async (userIds) => {
		const response = await apiFetch(`${config.API_URL}/api/users/login-seats`, {
			method: 'POST',
			body: JSON.stringify({ userIds }),
		})
		return parseJson<string>(response)
	},
}
